/**
 * Sound utility for handling notification sounds
 * Follows Single Responsibility Principle - only handles sound-related functionality
 */

type SoundSettings = {
  enabled: boolean
  volume: number
}

class NotificationSoundManager {
  private audio: HTMLAudioElement | null = null
  private settings: SoundSettings = {
    enabled: true,
    volume: 50,
  }
  private lastPlayTime: number = 0
  private readonly THROTTLE_DELAY_MS = 5000

  constructor() {
    this.loadSettings()
    this.initializeAudio()
  }

  /**
   * Initialize the audio element with the notification sound
   */
  private initializeAudio(): void {
    try {
      this.audio = new Audio('/sounds/flashcore.mp3')
      this.audio.preload = 'auto'
      this.audio.volume = this.settings.volume / 50
    } catch (error) {
      console.warn('Failed to initialize notification sound:', error)
    }
  }

  /**
   * Load sound settings from localStorage
   */
  private loadSettings(): void {
    try {
      const savedEnabled = localStorage.getItem('notificationSoundEnabled')
      const savedVolume = localStorage.getItem('notificationVolume')

      if (savedEnabled !== null) {
        this.settings.enabled = JSON.parse(savedEnabled)
      }
      if (savedVolume !== null) {
        this.settings.volume = parseInt(savedVolume, 10)
      }
    } catch (error) {
      console.warn('Failed to load sound settings:', error)
    }
  }

  /**
   * Save sound settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(
        'notificationSoundEnabled',
        JSON.stringify(this.settings.enabled),
      )
      localStorage.setItem(
        'notificationVolume',
        this.settings.volume.toString(),
      )
    } catch (error) {
      console.warn('Failed to save sound settings:', error)
    }
  }

  /**
   * Play the notification sound with throttling (max once every 5 seconds)
   */
  public playNotificationSound(): void {
    if (!this.settings.enabled || !this.audio) {
      return
    }

    const now = Date.now()
    const timeSinceLastPlay = now - this.lastPlayTime

    if (timeSinceLastPlay < this.THROTTLE_DELAY_MS) {
      return
    }

    try {
      this.lastPlayTime = now

      // Reset audio to beginning
      this.audio.currentTime = 0

      // Play the sound
      const playPromise = this.audio.play()

      // Handle promise-based play() method
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Failed to play notification sound:', error)
        })
      }
    } catch (error) {
      console.warn('Error playing notification sound:', error)
    }
  }

  /**
   * Enable or disable notification sounds
   */
  public setSoundEnabled(enabled: boolean): void {
    this.settings.enabled = enabled
    this.saveSettings()
  }

  /**
   * Set the volume for notification sounds (0-100)
   */
  public setVolume(volume: number): void {
    this.settings.volume = Math.max(0, Math.min(100, volume))

    if (this.audio) {
      this.audio.volume = this.settings.volume / 100
    }

    this.saveSettings()
  }

  /**
   * Get current sound settings
   */
  public getSettings(): SoundSettings {
    return { ...this.settings }
  }

  /**
   * Check if sound is enabled
   */
  public isSoundEnabled(): boolean {
    return this.settings.enabled
  }

  /**
   * Get current volume
   */
  public getVolume(): number {
    return this.settings.volume
  }

  /**
   * Test the notification sound (bypasses throttling for testing)
   */
  public testSound(): void {
    if (!this.settings.enabled || !this.audio) {
      return
    }

    try {
      this.audio.currentTime = 0

      const playPromise = this.audio.play()

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Failed to play notification sound:', error)
        })
      }
    } catch (error) {
      console.warn('Error playing notification sound:', error)
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    if (this.audio) {
      this.audio.pause()
      this.audio.src = ''
      this.audio = null
    }
  }
}

// Create a singleton instance
const notificationSoundManager = new NotificationSoundManager()

// Export the singleton instance and utility functions
export const notificationSound = {
  /**
   * Play notification sound
   */
  play: () => notificationSoundManager.playNotificationSound(),

  /**
   * Enable/disable sound
   */
  setEnabled: (enabled: boolean) =>
    notificationSoundManager.setSoundEnabled(enabled),

  /**
   * Set volume (0-100)
   */
  setVolume: (volume: number) => notificationSoundManager.setVolume(volume),

  /**
   * Get current settings
   */
  getSettings: () => notificationSoundManager.getSettings(),

  /**
   * Check if sound is enabled
   */
  isEnabled: () => notificationSoundManager.isSoundEnabled(),

  /**
   * Get current volume
   */
  getVolume: () => notificationSoundManager.getVolume(),

  /**
   * Test the sound
   */
  test: () => notificationSoundManager.testSound(),

  /**
   * Clean up (call when component unmounts)
   */
  destroy: () => notificationSoundManager.destroy(),
}

export default notificationSound
